import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { Toaster } from 'sonner';
import { QueryClientProvider } from '@tanstack/react-query';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { LoginPage, SignupPage, ProtectedRoute } from '@/features/auth';
import { LandingPage } from '@/features/landing/LandingPage';
import { queryClient } from '@/shared/services/query-client';

// Lazy-load every protected-route surface. This keeps recharts, xlsx, jspdf
// (and their downstream deps) out of the initial bundle that the login/signup
// screens need.
const AppLayout = lazy(() =>
  import('@/shared/components/layout/AppLayout').then((m) => ({ default: m.AppLayout })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const ReportsPage = lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.ReportsPage })),
);
const ReportDetailPage = lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.ReportDetailPage })),
);
const UploadPage = lazy(() =>
  import('@/features/upload').then((m) => ({ default: m.UploadPage })),
);
const ComparePage = lazy(() =>
  import('@/features/comparison').then((m) => ({ default: m.ComparePage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsPage })),
);
const ConnectionsPage = lazy(() =>
  import('@/features/connections').then((m) => ({ default: m.ConnectionsPage })),
);
const BillingPage = lazy(() =>
  import('@/features/billing').then((m) => ({ default: m.BillingPage })),
);

function RouteFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-sm text-muted-foreground">Loading…</div>
    </div>
  );
}

function App() {
  const isDarkMode = useAppStore((s) => s.isDarkMode);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/welcome" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="upload" element={<UploadPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="reports/:id" element={<ReportDetailPage />} />
                <Route path="compare" element={<ComparePage />} />
                <Route path="connections" element={<ConnectionsPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      </QueryClientProvider>
      <Toaster
        theme={isDarkMode ? 'dark' : 'light'}
        position="bottom-right"
        richColors
        closeButton
        duration={3000}
      />
    </ErrorBoundary>
  );
}

export default App;
